import React, { useEffect, useMemo, useState } from "react";
import { FiExternalLink, FiSearch } from "react-icons/fi";
import toast from "react-hot-toast";
import supportService from "../../api/services/supportService";
import Modal from "../../components/common/Modal";
import PaginationBar from "../../components/common/PaginationBar";
import { ListSkeleton } from "../../components/common/skeletons";

const THEME = { primary: "#FB565F" };
const PAGE_SIZE = 10;

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "PENDING", label: "Pending" },
  { key: "IN_PROGRESS", label: "In Progress" },
  { key: "COMPLETED", label: "Completed" },
  { key: "RESOLVED", label: "Resolved" },
  { key: "CLOSED", label: "Closed" },
];

const STATUS_STYLES = {
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  IN_PROGRESS: "bg-blue-100 text-blue-800 border-blue-200",
  COMPLETED: "bg-purple-100 text-purple-800 border-purple-200",
  RESOLVED: "bg-green-100 text-green-800 border-green-200",
  CLOSED: "bg-gray-100 text-gray-600 border-gray-200",
};

function isRequestAborted(error) {
  if (!error) return false;
  if (error.code === "ERR_CANCELED") return true;
  if (error.name === "CanceledError" || error.name === "AbortError") return true;
  return false;
}

function apiErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error?.message ||
    error?.message ||
    null
  );
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
        STATUS_STYLES[status] || STATUS_STYLES.CLOSED
      }`}
    >
      {status?.replace("_", " ")}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function SupportPerformanceTicketsModal({
  open,
  agent,
  status,
  onClose,
  onViewTicket,
  canViewClosedChats = false,
}) {
  /** null = use `status` prop from the clicked table cell */
  const [userStatus, setUserStatus] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const effectiveStatus = userStatus ?? status ?? "";
  const agentId = Number(agent?.agentId);

  useEffect(() => {
    if (!open) return;
    setUserStatus(null);
    setSearch("");
    setPage(1);
    setTickets([]);
    setTotal(0);
  }, [open, status, agentId]);

  const statusLabel = STATUS_TABS.find((t) => t.key === effectiveStatus)?.label || "All";

  useEffect(() => {
    if (!open || !Number.isFinite(agentId) || agentId <= 0) return undefined;

    const controller = new AbortController();
    const statusParam = effectiveStatus ? String(effectiveStatus).toUpperCase() : undefined;

    (async () => {
      setLoading(true);
      try {
        const data = await supportService.getAgentTickets({
          agentId,
          status: statusParam,
          search: search.trim() || undefined,
          page,
          limit: PAGE_SIZE,
          signal: controller.signal,
        });

        const rows = (data.tickets || []).filter(
          (t) => !statusParam || String(t.status).toUpperCase() === statusParam
        );

        setTickets(rows);
        setTotal(Number(data.total) || rows.length);
      } catch (error) {
        if (controller.signal.aborted || isRequestAborted(error)) return;
        toast.error(apiErrorMessage(error) || "Failed to load tickets");
        setTickets([]);
        setTotal(0);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [open, agentId, effectiveStatus, search, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleViewTicket = (ticket) => {
    if (ticket.status === "CLOSED" && !canViewClosedChats) {
      toast.error("Only Admin or Developer can view closed ticket chats.");
      return;
    }
    onViewTicket?.(ticket.id);
    onClose?.();
  };

  const inputCls =
    "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none";

  const activeTabKey = useMemo(() => effectiveStatus, [effectiveStatus]);

  return (
    <Modal
      open={open}
      title={`${agent?.name || "Agent"} — ${statusLabel} tickets`}
      onClose={onClose}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
        >
          Close
        </button>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">{agent?.name}</p>
          <p className="text-xs text-gray-500">{agent?.email}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {agent?.panelRole && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                {agent.panelRole}
              </span>
            )}
            {agent?.teamName && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                {agent.teamName}
                {agent?.isTeamLead ? " (Lead)" : ""}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key || "all"}
              type="button"
              onClick={() => {
                setUserStatus(tab.key);
                setPage(1);
              }}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                activeTabKey === tab.key
                  ? "text-white border-transparent shadow-sm"
                  : "bg-white text-gray-600 border-gray-200 hover:border-red-300"
              }`}
              style={activeTabKey === tab.key ? { backgroundColor: THEME.primary } : {}}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search tickets by name, email, phone, or message…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className={`${inputCls} w-full pl-9`}
          />
        </div>

        {loading ? (
          <ListSkeleton rows={6} />
        ) : tickets.length === 0 ? (
          <p className="text-center text-gray-500 py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            No {statusLabel.toLowerCase()} tickets found for this agent.
          </p>
        ) : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <div
                key={t.id}
                className="rounded-xl border border-gray-200 bg-white p-3 hover:border-red-200 hover:bg-red-50/20 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">
                      {t.displayName || t.guest_name || "Support User"}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      #{t.id} · {t.displayEmail || "—"} · {t.displayPhone || "—"}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                      {t.last_message || t.message || "No messages yet"}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                      <StatusBadge status={t.status} />
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
                          t.session_type === "GUEST"
                            ? "bg-sky-50 text-sky-700 border-sky-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200"
                        }`}
                      >
                        {t.session_type === "GUEST" ? "Guest" : "Logged in"}
                      </span>
                      <span className="text-[10px] text-gray-400">{formatDate(t.updated_at)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleViewTicket(t)}
                    disabled={t.status === "CLOSED" && !canViewClosedChats}
                    className="inline-flex items-center gap-1 shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ backgroundColor: THEME.primary }}
                  >
                    <FiExternalLink /> Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <PaginationBar
            page={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            label="tickets"
            onPageChange={setPage}
          />
        )}
      </div>
    </Modal>
  );
}
